'use client';

import { IFile, IImage } from '@sapientpro/sapientpro-data-models';
import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './StoreCard.module.scss';
import { theme } from '../../../store/theme';
import clientDeviceSize, { ScreenSize } from '../../../store/deviceScreenSize';

type StoreCardItem = {
  title: string;
  description: string;
  icon: { dark: IImage; light: IImage };
};

type StoreCardProps = {
  video: { light: IFile; dark: IFile };
  title: string;
  description: string;
  items?: StoreCardItem[];
  removeLeftBorder?: boolean;
};

const StoreCard = ({
  video,
  title,
  description,
  items,
  removeLeftBorder,
}: StoreCardProps) => {
  const appTheme = useAtomValue(theme);
  const deviceSize = useAtomValue<ScreenSize>(clientDeviceSize);
  const contentInfoRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  const isMobile = (deviceSize <= ScreenSize.TABLET_PORTRAIT);

  const isItemsListEmpty = items?.length === 0;

  useEffect(() => {
    if (!contentInfoRef.current || isItemsListEmpty) return;

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setHeight(entry.contentRect.height);
      });
    });

    observer.observe(contentInfoRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      className={classNames(styles.storeCard, {
        [styles.storeCard_removeLeftBorder]: removeLeftBorder,
        [styles.storeCard_noList]: isItemsListEmpty,
      })}
      style={{ minHeight: `calc(${height}px + ${isMobile ? 477 : 501}px)` }}
    >
      <div className={styles.storeCard__videoWrapper}>
        <video
          className={styles.storeCard__video}
          src={video[appTheme].url}
          autoPlay
          loop
          muted
          playsInline
        />
      </div>
      <div className={styles.storeCard__content}>
        <div
          ref={contentInfoRef}
          className={styles.storeCard__contentInfo}
        >
          <div className={styles.storeCard__contentInfoHeader}>
            <h3 className={classNames(styles.storeCard__contentInfoHeaderTitle, 'header5')}>{title}</h3>
            {!isItemsListEmpty && (
              <svg className={styles.storeCard__contentInfoHeaderIcon}>
                <use
                  xlinkHref='/media/arrow.svg#arrowSVG'
                  href='/media/arrow.svg#arrowSVG'
                />
              </svg>
            )}
          </div>
          <p className={styles.storeCard__contentInfoDescription}>{description}</p>
        </div>
        {!isItemsListEmpty && (
          <ul className={styles.storeCard__contentList}>
            {items?.map((item) => (
              <li
                key={item.title}
                className={styles.storeCard__contentListItem}
              >
                <div className={styles.storeCard__contentListItemMain}>
                  <img
                    className={styles.storeCard__contentListItemMainIcon}
                    src={item.icon[appTheme].url}
                    alt={item.icon[appTheme].alternativeText ?? 'Item icon'}
                  />
                  <h4 className={styles.storeCard__contentListItemMainTitle}>{item.title}</h4>
                </div>
                <p className={styles.storeCard__contentListItemDescription}>
                  {item.description}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default StoreCard;
